import { createServerFn } from "@tanstack/react-start";
import { env } from "cloudflare:workers";
import { Resend } from "resend";

type ContactMessage = {
  name: string;
  email: string;
  company: string;
  topic: string;
  message: string;
};

function clean(value: unknown, field: string, maxLength: number) {
  if (typeof value !== "string") throw new Error(`Please enter your ${field}.`);
  const result = value.trim();
  if (!result) throw new Error(`Please enter your ${field}.`);
  if (result.length > maxLength)
    throw new Error(`${field[0].toUpperCase()}${field.slice(1)} is too long.`);
  return result;
}

function validateContactMessage(value: ContactMessage) {
  const email = clean(value.email, "email address", 254).toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    throw new Error("Please enter a valid email address.");
  return {
    name: clean(value.name, "name", 120),
    email,
    company:
      typeof value.company === "string"
        ? value.company.trim().slice(0, 160)
        : "",
    topic: clean(value.topic, "topic", 100),
    message: clean(value.message, "message", 5000),
  };
}

function escapeHtml(value: string) {
  return value.replace(
    /[&<>'"]/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[
        character
      ] ?? character,
  );
}

function contactEmailHtml(message: ContactMessage) {
  const fields = [
    ["Name", message.name],
    ["Email", message.email],
    ["Company", message.company || "Not provided"],
    ["Topic", message.topic],
  ];
  return `<!doctype html><html><body style="margin:0;background:#f7f5ef;color:#10251f;font-family:Arial,sans-serif"><div style="max-width:680px;margin:32px auto;padding:32px"><div style="border-radius:24px;background:#10251f;padding:28px 32px;color:#f7f5ef"><p style="margin:0;color:#b7f36b;font-size:12px;font-weight:700;letter-spacing:1.5px">HOSTRIGHT CONTACT</p><h1 style="margin:12px 0 0;font-size:32px">A new message has arrived.</h1></div><div style="background:#ffffff;border:1px solid #dce9e2;border-radius:24px;margin-top:16px;padding:28px 32px"><table style="border-collapse:collapse;width:100%;font-size:15px">${fields.map(([label, value]) => `<tr><td style="padding:10px 0;color:#5d6d67;width:120px">${label}</td><td style="padding:10px 0;font-weight:700">${escapeHtml(value)}</td></tr>`).join("")}</table><div style="border-top:1px solid #dce9e2;margin-top:18px;padding-top:20px"><p style="margin:0 0 8px;color:#5d6d67;font-size:12px;font-weight:700;letter-spacing:1.2px">MESSAGE</p><p style="margin:0;line-height:1.7;white-space:pre-wrap">${escapeHtml(message.message)}</p></div></div></div></body></html>`;
}

function confirmationEmailHtml(message: ContactMessage) {
  return `<!doctype html><html><body style="margin:0;background:#f7f5ef;color:#10251f;font-family:Arial,sans-serif"><div style="max-width:680px;margin:32px auto;padding:32px"><div style="border-radius:24px;background:#10251f;padding:28px 32px;color:#f7f5ef"><p style="margin:0;color:#b7f36b;font-size:12px;font-weight:700;letter-spacing:1.5px">HOSTRIGHT</p><h1 style="margin:12px 0 0;font-size:32px">We have received your message.</h1></div><div style="background:#ffffff;border:1px solid #dce9e2;border-radius:24px;margin-top:16px;padding:28px 32px"><p style="margin:0;font-size:17px;line-height:1.7">Hi ${escapeHtml(message.name)},</p><p style="margin:18px 0 0;font-size:17px;line-height:1.7">Thanks for getting in touch about ${escapeHtml(message.topic)}. We will review your message and get back to you swiftly.</p><div style="margin-top:24px;border-radius:16px;background:#e6f0eb;padding:18px"><p style="margin:0;color:#1d5d49;font-size:12px;font-weight:700;letter-spacing:1.2px">YOUR MESSAGE</p><p style="margin:8px 0 0;line-height:1.7;white-space:pre-wrap">${escapeHtml(message.message)}</p></div><p style="margin:24px 0 0;color:#5d6d67;font-size:14px;line-height:1.6">HostRight</p></div></div></body></html>`;
}

export const sendContactMessage = createServerFn({ method: "POST" })
  .validator((data: ContactMessage) => validateContactMessage(data))
  .handler(async ({ data }) => {
    const runtime = env as unknown as Record<string, string | undefined>;
    const apiKey = runtime.RESEND_API_KEY;
    const recipient = runtime.ADMIN_EMAIL;
    const from = runtime.EMAIL_FROM;
    if (!apiKey || !recipient || !from)
      throw new Error(
        "The contact form is not configured yet. Please try again shortly.",
      );

    const resend = new Resend(apiKey);
    const internal = await resend.emails.send({
      from,
      to: recipient,
      replyTo: data.email,
      subject: `[HostRight] ${data.topic} from ${data.name}`,
      text: `Name: ${data.name}\nEmail: ${data.email}\nCompany: ${data.company || "Not provided"}\nTopic: ${data.topic}\n\n${data.message}`,
      html: contactEmailHtml(data),
    });
    if (internal.error)
      throw new Error(
        "We could not send your message. Please try again shortly.",
      );

    const confirmation = await resend.emails.send({
      from,
      to: data.email,
      subject: "We received your HostRight message",
      text: `Hi ${data.name},\n\nThanks for getting in touch about ${data.topic}. We will review your message and get back to you swiftly.\n\nHostRight`,
      html: confirmationEmailHtml(data),
    });
    if (confirmation.error)
      throw new Error(
        "We could not send your confirmation. Please try again shortly.",
      );
    return { sent: true };
  });
